<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <div class="mb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <h1 class="text-[26px] sm:text-[24px] leading-7 font-bold text-gray-900">
        {{ pageTitle }}
      </h1>

      <div v-if="showHeaderButton && !loading">
        <button
          type="button"
          :disabled="submitting"
          class="h-10 px-6 rounded-full text-white font-semibold shadow-sm hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style="background-color:#5B9AC5"
          @click="handleHeaderButton"
        >
          <span v-if="submitting" class="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
          {{ submitting ? 'Saving...' : headerButtonLabel }}
        </button>
      </div>
    </div>

    <div class="mb-6"></div>

    <div v-if="loading" class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden p-8">
      <div class="max-w-3xl mx-auto space-y-5">
        <div v-for="i in 8" :key="i" class="space-y-2">
          <div class="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
          <div class="h-10 w-full bg-gray-100 rounded-xl animate-pulse"></div>
        </div>
      </div>
    </div>

    <div v-else class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <DynamicForm
        :schema="schema"
        :modelValue="form"
        @update:modelValue="onFormUpdate"
        :mode="mode"
        :submitLabel="submitLabel"
        :cancelLabel="'Cancel'"
        :submitColor="'#5B9AC5'"
        :showActions="false"
        @submit="handleSubmit"
        @cancel="handleCancel"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router';
import api from '@/api';
import DynamicForm from '@/components/DynamicForm.vue';
import Swal from 'sweetalert2';

const route  = useRoute();
const router = useRouter();

const CHECKS_ROUTE = 'Checks';

const loading = ref(false);
const submitting = ref(false);

const mode = computed(() => {
  const m = String(route.query.mode || 'add').toLowerCase();
  return ['add','edit','view'].includes(m) ? m : 'add';
});
const recordId = computed(() => route.query.id ?? null);

/* ===== Auth ===== */
const authConfig = computed(() => {
  const token = localStorage.getItem('token');
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
});

/* ===== Arabic number-to-words ===== */
const ones = [
  '', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة',
  'عشرة', 'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر',
  'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر',
];
const tensWords     = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
const hundredsWords = ['', 'مائة', 'مئتان', 'ثلاثمائة', 'أربعمائة', 'خمسمائة', 'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة'];

function toWords(n) {
  if (n === 0) return 'صفر';
  if (n < 20)  return ones[n];
  if (n < 100) {
    const t = Math.floor(n / 10);
    const o = n % 10;
    return o ? `${ones[o]} و${tensWords[t]}` : tensWords[t];
  }
  if (n < 1000) {
    const h    = Math.floor(n / 100);
    const rest = n % 100;
    return rest ? `${hundredsWords[h]} و${toWords(rest)}` : hundredsWords[h];
  }
  if (n < 1_000_000) {
    const th   = Math.floor(n / 1000);
    const rest = n % 1000;
    const thWord =
      th === 1 ? 'ألف' :
      th === 2 ? 'ألفان' :
      th <= 10  ? `${ones[th]} آلاف` :
                  `${toWords(th)} ألف`;
    return rest ? `${thWord} و${toWords(rest)}` : thWord;
  }
  if (n < 1_000_000_000) {
    const m    = Math.floor(n / 1_000_000);
    const rest = n % 1_000_000;
    const mWord =
      m === 1 ? 'مليون' :
      m === 2 ? 'مليونان' :
                `${toWords(m)} مليون`;
    return rest ? `${mWord} و${toWords(rest)}` : mWord;
  }
  return String(n);
}

function numberToArabicWords(num) {
  if (num === null || num === undefined || num === '' || isNaN(Number(num))) return '';

  const value = Math.abs(Number(num));
  const numStr = parseFloat(value.toFixed(10)).toString();

  const [integerStr, decimalStr = ''] = numStr.split('.');
  const integer = parseInt(integerStr, 10);

  let result = toWords(integer) + ' دينار';

  if (decimalStr && parseInt(decimalStr) !== 0) {
    const decimalNum = parseInt(decimalStr, 10);
    result += ` و${toWords(decimalNum)} فلس`;
  }

  return result + ' فقط لا غير';
}
/* ===== Companies from API ===== */
const companiesOptions = ref([]);

async function fetchCompanies() {
  try {
    const res  = await api.get('/accounting/companies', authConfig.value);
    const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
    companiesOptions.value = data.map(c => ({ label: c.name, value: String(c.id) }));
  } catch (e) {
    console.error(e);
  }
}

onBeforeRouteLeave((to) => {
  const checksRoutes = ['Checks', 'CheckUpsertView', 'CheckPrint'];
  if (!checksRoutes.includes(to.name)) {
    sessionStorage.removeItem('checks_selected_company_id');
    sessionStorage.removeItem('checks_selected_company_name');
  }
});

/* ===== Meta (petty_cash, suppliers, banks) from API ===== */
const pettyCashOptions = ref([]);
const suppliersOptions = ref([]);
const banksOptions     = ref([]);
const employeesOptions = ref([]);

async function fetchMeta(companyId) {
  if (!companyId) return;
  try {
    const res = await api.get('/accounting/checks/meta', {
      ...authConfig.value,
      params: { company_id: companyId },
    });
    const d = res.data || {};
    pettyCashOptions.value = (d.petty_cash || []).map(p => ({ label: p.employee_name, value: String(p.id) }));
    suppliersOptions.value = (d.suppliers   || []).map(s => ({ label: s.supplier_name, value: String(s.id) }));
    banksOptions.value     = (d.banks        || []).map(b => ({ label: b.name,          value: String(b.id) }));
  } catch (e) {
    console.error(e);
  }
}

/* ===== Fetch Employees for Company ===== */
async function fetchEmployees(companyId) {
  if (!companyId) {
    employeesOptions.value = [];
    return;
  }
  try {
    const res = await api.get(`/companies/${companyId}/employees`, authConfig.value);
    const employeesData = res.data?.employees || [];
    employeesOptions.value = employeesData.map(emp => ({
      label: emp.name,
      value: String(emp.id)
    }));
  } catch (e) {
    console.error('Failed to fetch employees:', e);
    employeesOptions.value = [];
  }
}

/* ===== Static options ===== */
const paymentMethods = [
  { label: 'Check',       value: 'check' },
  { label: 'Cash',        value: 'cash' },
  { label: 'Credit Card', value: 'credit_card' },
];
const paymentForOptions = [
  { label: 'Purchasing Fund', value: 'purchasing_fund' },
  { label: 'Supplier',        value: 'supplier' },
  { label: 'Employee',        value: 'employee' },
];

/* ===== Form ===== */
const form = ref({
  company:        '',
  companyDisplay: '',
  paymentMethod:  '',
  checkNo:        '',
  creditCardNo:   '',
  date:           '',
  paymentFor:     '',
  total:          '',
  totalInWord:    '',
  bankId:         '',
  bankName:       '',
  details:        '',
  pettyCash:      '',
  supplier:       '',
  salary_month:    '',
  employeeName:   '',
});

function onFormUpdate(val) {
  form.value = { ...form.value, ...val };
}

/* ===== Schema ===== */
const schema = computed(() => ({
  sections: [
    {
      cols: 1,
      fields: [
        {
          key: 'companyDisplay',
          label: 'For Company',
          type: 'text',
          disabledIf: (m) => true,
          placeholder: '(company name)'
        },
        { key: 'paymentMethod', label: 'Payment Methods', type: 'select', options: paymentMethods,         placeholder: 'Select Payment Methods' },
        { key: 'checkNo',       label: 'Check No.',       type: 'text',   placeholder: 'Enter check no.',       visibleIf: (m) => m.paymentMethod === 'check' },
        { key: 'creditCardNo',  label: 'Credit Card No.', type: 'text',   placeholder: 'Enter credit card no.', visibleIf: (m) => m.paymentMethod === 'credit_card' },
      ],
    },
    {
      title: 'Check Details',
      cols: 1,
      fields: [
        { key: 'date',          label: 'Date',          type: 'date' },
        { key: 'paymentFor',    label: 'Payment For',   type: 'select',   options: paymentForOptions, placeholder: 'Select payment for' },
        {
          key: 'pettyCash',
          label: 'Petty Cash',
          type: 'select',
          options: pettyCashOptions.value,
          placeholder: 'Select petty cash',
          visibleIf: (m) => m.paymentFor === 'purchasing_fund',
        },
        { key: 'supplier',     label: 'Supplier',      type: 'select',   options: suppliersOptions.value, placeholder: 'Select supplier',      visibleIf: (m) => m.paymentFor === 'supplier' },
        {
          key: 'salary_month',
          label: 'Salary Month',
          type: 'month',
          visibleIf: (m) => m.paymentFor === 'employee'
        },
        {
          key: 'employeeName',
          label: 'Employee Name',
          type: 'select',
          options: employeesOptions.value,
          placeholder: 'Select employee name',
          visibleIf: (m) => m.paymentFor === 'employee'
        },
        { key: 'total',        label: 'Total',         type: 'number',   placeholder: 'Enter the total amount', step: 'any' },
        { key: 'totalInWord',  label: 'Total in Word', type: 'text',     placeholder: 'Total amount in word' },
        { key: 'bankId',       label: 'Bank Name',     type: 'select',   options: banksOptions.value, placeholder: 'Select Bank Name' },
        { key: 'details',      label: 'Check Details', type: 'textarea', placeholder: 'Enter check details' },
      ],
    },
  ],
}));

/* ===== Keep dynamic options in sync after async fetch ===== */
watch(pettyCashOptions, (val) => {
  const f = schema.value.sections[1].fields.find(f => f.key === 'pettyCash');
  if (f) f.options = val;
});
watch(suppliersOptions, (val) => {
  const f = schema.value.sections[1].fields.find(f => f.key === 'supplier');
  if (f) f.options = val;
});
watch(banksOptions, (val) => {
  const f = schema.value.sections[1].fields.find(f => f.key === 'bankId');
  if (f) f.options = val;
});
watch(employeesOptions, (val) => {
  const f = schema.value.sections[1].fields.find(f => f.key === 'employeeName');
  if (f) f.options = val;
});
watch(companiesOptions, (val) => {
  if (form.value.company) {
    const matched = val.find(c => c.value === String(form.value.company));
    if (matched) form.value.companyDisplay = matched.label;
  }
});

/* ===== Auto-fill Total in Word when Total changes ===== */
watch(
  () => form.value.total,
  (val) => {
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) {
      form.value = { ...form.value, totalInWord: numberToArabicWords(num) };
    } else if (val === '' || val === null || val === undefined || val == 0) {
      form.value = { ...form.value, totalInWord: '' };
    }
  },
);

/* ===== Computed ===== */
const pageTitle         = computed(() => mode.value === 'edit' ? 'Edit Check' : mode.value === 'view' ? 'View Check' : 'Add Check');
const submitLabel       = computed(() => mode.value === 'edit' ? 'Update' : 'Save');
const headerButtonLabel = computed(() => mode.value === 'add'  ? 'Add New Check' : 'Save');
const showHeaderButton  = computed(() => mode.value !== 'view');

/* ===== Payload ===== */
function buildPayload() {
  const f = form.value;

  const totalRaw = parseFloat(f.total) || 0;
  const total = Math.round(totalRaw * 1000) / 1000;
  const payload = {
    company_id:     String(f.company),
    company_name:   companiesOptions.value.find(c => c.value === String(f.company))?.label || f.companyDisplay || '',
    payment_method: f.paymentMethod,
    check_id:       f.checkNo || f.creditCardNo || null,
    date:           f.date         || null,
    payment_for:    f.paymentFor,
    total:          total,
    total_in_word:  f.totalInWord  || null,
    bank_id:        f.bankId       || null,
    check_details:  f.details      || null,
  };

  if (f.paymentFor === 'purchasing_fund') {
    payload.payment_for_id = f.pettyCash ? Number(f.pettyCash) : null;
  } else if (f.paymentFor === 'supplier') {
    payload.payment_for_id = f.supplier  ? Number(f.supplier)  : null;
  } else if (f.paymentFor === 'employee') {
      const matchedEmployee = employeesOptions.value.find(e => e.value === String(f.employeeName));
      payload.payment_for_id   = f.employeeName ? Number(f.employeeName) : null;
      payload.payment_for_name = matchedEmployee ? matchedEmployee.label : null;
      payload.salary_month      = f.salary_month ? `${f.salary_month}-01` : null;
    }

  return payload;
}
/* ===== Load record (edit/view) ===== */
async function loadRecord(id) {
  try {
    const res = await api.get(`/accounting/checks/${id}`, authConfig.value);
    const c   = res.data;
    await fetchMeta(c.company_id);
    await fetchEmployees(c.company_id);

    const totalRaw     = parseFloat(c.total ?? 0);
    const totalDisplay = Number.isInteger(totalRaw) ? totalRaw : totalRaw;
    form.value = {
      company:        String(c.company_id   || ''),
      companyDisplay: c.company_name        || '',
      paymentMethod:  c.payment_method      || '',
      checkNo:        c.payment_method === 'check'       ? (c.check_id || '') : '',
      creditCardNo:   c.payment_method === 'credit_card' ? (c.check_id || '') : '',
      date:           c.date                || '',
      paymentFor:     c.payment_for         || '',
      total:          totalDisplay,
      totalInWord:    c.total_in_word       || numberToArabicWords(totalRaw),
      bankId:         String(c.bank_id      || ''),
      bankName:       c.bank_name           || '',
      details:        c.check_details       || '',
      pettyCash:      c.payment_for === 'purchasing_fund' ? String(c.payment_for_id || '') : '',
      supplier:       c.payment_for === 'supplier'        ? String(c.payment_for_id || '') : '',
      employeeName:   c.payment_for === 'employee'        ? String(c.payment_for_id || '') : '',
      salary_month:   c.salary_month ? c.salary_month.substring(0, 7) : '',
    };
  } catch (e) {
    console.error(e);
  }
}

/* ===== Lifecycle ===== */
onMounted(async () => {
  loading.value = true;
  try {
    await fetchCompanies();

    const st        = window.history.state || {};
    const companyId = st.checkCompanyId || sessionStorage.getItem('checks_selected_company_id') || '';

    if (mode.value === 'add') {
      if (companyId) {
        await fetchMeta(companyId);
        await fetchEmployees(companyId);
        form.value = { ...form.value, company: companyId };

        const matched = companiesOptions.value.find(c => c.value === String(companyId));
        if (matched) {
          form.value.companyDisplay = matched.label;
        }
      }
    } else if (recordId.value) {
      await loadRecord(recordId.value);
    }
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
});

watch([mode, recordId], async () => {
  if (mode.value !== 'add' && recordId.value) {
    loading.value = true;
    try {
      await loadRecord(recordId.value);
    } finally {
      loading.value = false;
    }
  }
});

watch(() => form.value.company, async (val) => {
  if (val && mode.value === 'add') {
    await fetchMeta(val);
    await fetchEmployees(val);
    const matched = companiesOptions.value.find(c => c.value === String(val));
    if (matched) {
      form.value.companyDisplay = matched.label;
    }
  }
});

/* ===== Actions ===== */
async function handleHeaderButton() {
  if (mode.value === 'view' || submitting.value) return;

  submitting.value = true;

  if (mode.value === 'edit' && recordId.value) {
    try {
      const payload = buildPayload();
      await api.put(`/accounting/checks/${recordId.value}`, payload, authConfig.value);

      await Swal.fire({
        icon: 'success',
        title: 'Updated successfully',
        toast: true,
        position: 'top',
        timer: 1400,
        showConfirmButton: false,
      });

      router.push({ name: CHECKS_ROUTE });
      return;
    } catch (e) {
      submitting.value = false;
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: e?.response?.data?.message || e?.message || 'Failed to update check.',
        toast: true,
        position: 'top',
        timer: 2200,
        showConfirmButton: false,
      });
      return;
    }
  }

  const payload = buildPayload();
  try {
    const res = await api.post('/accounting/checks', payload, authConfig.value);

    await Swal.fire({
      icon: 'success',
      title: res?.data?.message || 'Saved successfully',
      toast: true,
      position: 'top',
      timer: 1400,
      showConfirmButton: false,
    });

    router.push({ name: CHECKS_ROUTE });
  } catch (e) {
    submitting.value = false;
    await Swal.fire({
      icon: 'error',
      title: 'Error',
      text: e?.response?.data?.message || e?.message || 'Failed to save check.',
      toast: true,
      position: 'top',
      timer: 2200,
      showConfirmButton: false,
    });
  }
}

async function handleSubmit() {
  await handleHeaderButton();
}

function handleCancel() {
  router.back();
}
</script>
